import { type ResolvedKeybindingsConfig } from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import ThreadSidebar from "../components/Sidebar";
import { isElectron } from "../env";
import { useDisposableThreadLifecycle } from "../hooks/useDisposableThreadLifecycle";
import { useHandleNewThread } from "../hooks/useHandleNewThread";
import { resolveThreadEnvironmentMode } from "../lib/threadEnvironment";
import { isTerminalFocused } from "../lib/terminalFocus";
import { serverConfigQueryOptions } from "../lib/serverReactQuery";
import { resolveShortcutCommand } from "../keybindings";
import { selectThreadTerminalState, useTerminalStateStore } from "../terminalStateStore";
import { useThreadSelectionStore } from "../threadSelectionStore";
import { NewThreadDialogContainer } from "../components/NewThreadDialogContainer";
import { openNewThreadDialog } from "../newThreadDialogStore";
import { useAppSettings } from "~/appSettings";
import { Sidebar, SidebarProvider, SidebarRail, useSidebar } from "~/components/ui/sidebar";
import { useChatCodeFont } from "~/hooks/useChatCodeFont";
import { useUIFont } from "~/hooks/useUIFont";

const EMPTY_KEYBINDINGS: ResolvedKeybindingsConfig = [];
const THREAD_SIDEBAR_WIDTH_STORAGE_KEY = "chat_thread_sidebar_width";
const THREAD_SIDEBAR_MIN_WIDTH = 13 * 16;
const THREAD_MAIN_CONTENT_MIN_WIDTH = 40 * 16;

function ChatRouteGlobalShortcuts() {
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const clearSelection = useThreadSelectionStore((state) => state.clearSelection);
  const selectedThreadIdsSize = useThreadSelectionStore((state) => state.selectedThreadIds.size);
  const {
    activeContextThreadId,
    activeDraftThread,
    activeProjectId,
    activeThread,
    handleNewThread,
    projects,
  } = useHandleNewThread();
  useDisposableThreadLifecycle(activeContextThreadId);
  const serverConfigQuery = useQuery(serverConfigQueryOptions());
  const keybindings = serverConfigQuery.data?.keybindings ?? EMPTY_KEYBINDINGS;
  const terminalOpen = useTerminalStateStore((state) =>
    activeContextThreadId
      ? selectThreadTerminalState(state.terminalStateByThreadId, activeContextThreadId).terminalOpen
      : false,
  );
  const { settings: appSettings } = useAppSettings();

  useEffect(() => {
    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      if (event.key === "Escape" && selectedThreadIdsSize > 0) {
        event.preventDefault();
        clearSelection();
        return;
      }

      const command = resolveShortcutCommand(event, keybindings, {
        context: {
          terminalFocus: isTerminalFocused(),
          terminalOpen,
        },
      });
      if (command === "sidebar.toggle") {
        if (!isElectron) return;
        event.preventDefault();
        event.stopPropagation();
        toggleSidebar();
        return;
      }

      if (!command) return;

      if (command === "chat.newLocal") {
        const projectId = activeProjectId ?? projects[0]?.id;
        if (!projectId) return;
        event.preventDefault();
        event.stopPropagation();
        openNewThreadDialog({ preferredProjectId: projectId });
        return;
      }

      if (command === "chat.newTerminal") {
        const projectId = activeProjectId ?? projects[0]?.id;
        if (!projectId) return;
        event.preventDefault();
        event.stopPropagation();
        void handleNewThread(projectId, {
          branch: activeThread?.branch ?? activeDraftThread?.branch ?? null,
          worktreePath: activeThread?.worktreePath ?? activeDraftThread?.worktreePath ?? null,
          envMode:
            activeDraftThread?.envMode ??
            resolveThreadEnvironmentMode({
              envMode: activeThread?.envMode,
              worktreePath: activeThread?.worktreePath ?? null,
            }),
          entryPoint: "terminal",
        });
        return;
      }

      if (command === "chat.newClaude" || command === "chat.newCodex") {
        const projectId = activeProjectId ?? projects[0]?.id;
        if (!projectId) return;
        event.preventDefault();
        event.stopPropagation();
        openNewThreadDialog({
          preferredProjectId: projectId,
          initialProvider: command === "chat.newClaude" ? "claudeAgent" : "codex",
        });
        return;
      }

      if (command !== "chat.new") return;
      const projectId = activeProjectId ?? projects[0]?.id;
      if (!projectId) return;
      event.preventDefault();
      event.stopPropagation();
      openNewThreadDialog({ preferredProjectId: projectId });
    };

    window.addEventListener("keydown", onWindowKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", onWindowKeyDown, { capture: true });
    };
  }, [
    activeDraftThread,
    activeProjectId,
    activeThread,
    clearSelection,
    handleNewThread,
    keybindings,
    projects,
    selectedThreadIdsSize,
    terminalOpen,
    toggleSidebar,
    appSettings.defaultThreadEnvMode,
  ]);

  useEffect(() => {
    const onMenuAction = window.desktopBridge?.onMenuAction;
    if (typeof onMenuAction !== "function") {
      return;
    }

    const unsubscribe = onMenuAction((action) => {
      if (action === "toggle-sidebar") {
        toggleSidebar();
        return;
      }
      if (action !== "open-settings") return;
      void navigate({ to: "/settings" });
    });

    return () => {
      unsubscribe?.();
    };
  }, [navigate, toggleSidebar]);

  return null;
}

const SIDEBAR_GAP_CLASS = {
  left: "overflow-hidden after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-black/[0.03] dark:after:bg-white/[0.015] before:absolute before:inset-0 before:bg-[radial-gradient(90%_75%_at_0%_0%,rgba(255,255,255,0.06),transparent_58%),linear-gradient(180deg,rgba(255,255,255,0.025),rgba(255,255,255,0.008))] dark:before:bg-[radial-gradient(90%_75%_at_0%_0%,rgba(255,255,255,0.04),transparent_58%),linear-gradient(180deg,rgba(255,255,255,0.018),rgba(255,255,255,0.006))]",
  right:
    "overflow-hidden after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-black/[0.03] dark:after:bg-white/[0.015] before:absolute before:inset-0 before:bg-[radial-gradient(90%_75%_at_100%_0%,rgba(255,255,255,0.06),transparent_58%),linear-gradient(180deg,rgba(255,255,255,0.025),rgba(255,255,255,0.008))] dark:before:bg-[radial-gradient(90%_75%_at_100%_0%,rgba(255,255,255,0.04),transparent_58%),linear-gradient(180deg,rgba(255,255,255,0.018),rgba(255,255,255,0.006))]",
} as const;

const SIDEBAR_INNER_CLASS = {
  left: "border-r border-border bg-background/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-lg backdrop-saturate-150 dark:border-white/[0.06] dark:bg-background/70 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]",
  right:
    "border-l border-border bg-background/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-lg backdrop-saturate-150 dark:border-white/[0.06] dark:bg-background/70 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]",
} as const;

/**
 * On mobile the sidebar is a drawer covering the whole screen, so picking a
 * thread from it navigates behind the drawer and leaves the user staring at the
 * list they just chose from. Desktop keeps it open on purpose — there it is a
 * persistent column, not an overlay.
 */
function MobileSidebarAutoClose() {
  const { isMobile, setOpenMobile } = useSidebar();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    setOpenMobile(false);
  }, [isMobile, pathname, setOpenMobile]);

  return null;
}

function ChatRouteLayout() {
  useChatCodeFont();
  useUIFont();
  const { settings } = useAppSettings();
  const side = settings.sidebarSide;

  const sidebarElement = (
    <Sidebar
      side={side}
      collapsible="offcanvas"
      className="text-foreground"
      gapClassName={SIDEBAR_GAP_CLASS[side]}
      innerClassName={SIDEBAR_INNER_CLASS[side]}
      transparentSurface
      resizable={{
        minWidth: THREAD_SIDEBAR_MIN_WIDTH,
        shouldAcceptWidth: ({ nextWidth, wrapper }) =>
          wrapper.clientWidth - nextWidth >= THREAD_MAIN_CONTENT_MIN_WIDTH,
        storageKey: THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
      }}
    >
      <ThreadSidebar />
      <SidebarRail />
    </Sidebar>
  );

  return (
    <SidebarProvider defaultOpen>
      <ChatRouteGlobalShortcuts />
      <MobileSidebarAutoClose />
      <NewThreadDialogContainer />
      {side === "left" ? sidebarElement : null}
      <Outlet />
      {side === "right" ? sidebarElement : null}
    </SidebarProvider>
  );
}

export const Route = createFileRoute("/_chat")({
  component: ChatRouteLayout,
});
